import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FiAlertCircle,
  FiArrowLeft,
  FiArrowRight,
  FiBriefcase,
  FiCalendar,
  FiCheck,
  FiClock,
  FiCreditCard,
  FiDollarSign,
  FiDownload,
  FiFile,
  FiInfo,
  FiLink,
  FiMessageSquare,
  FiPaperclip,
  FiRefreshCw,
  FiSend,
} from 'react-icons/fi';
import { api } from '../services/api';
import { useLocale } from '../context/LocaleContext';
import {
  formatServiceOrderAmount,
  formatServiceOrderDate,
  nextServicePaymentStage,
  paymentStatusLabelLocalized,
  serviceOrderId,
  statusInfoLocalized,
} from '../utils/serviceOrders';
import './UserServiceOrders.css';

const entityId = (entity) => String(entity?._id || entity?.id || '');
const HISTORY_PAGE_SIZE = 7;

const initialPaymentMode = (order = {}) => {
  const currentMode = order.paymentSchedule?.mode || '';
  const options = Array.isArray(order.paymentOptions) ? order.paymentOptions : [];

  if (options.length === 1 && !options.some((option) => option.id === currentMode)) {
    return options[0].id;
  }

  return currentMode;
};

const answerValue = (value, t) => {
  if (Array.isArray(value)) return value.join('، ');
  if (typeof value === 'boolean') return value ? t('customerUi.serviceOrders.yes') : t('customerUi.serviceOrders.no');
  return String(value ?? '—');
};

const UserServiceOrders = ({
  orderId = '',
  onOpenOrder,
  onCloseOrder,
  onCountChange,
}) => {
  const { t } = useTranslation();
  const { locale, localePath, formatNumber } = useLocale();
  const numberLocale = locale.intlLocale || locale.code;
  const paymentModeCopy = {
    full: {
      title: t('customerUi.serviceOrders.payment.full'),
      description: t('customerUi.serviceOrders.payment.fullHint'),
    },
    two_stage: {
      title: t('customerUi.serviceOrders.payment.initial'),
      description: t('customerUi.serviceOrders.payment.initialHint'),
    },
  };
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState('');
  const [notice, setNotice] = useState(null);
  const [detailError, setDetailError] = useState('');
  const [filter, setFilter] = useState('active');
  const [message, setMessage] = useState('');
  const [messageFiles, setMessageFiles] = useState([]);
  const [historyPage, setHistoryPage] = useState(1);
  const [selectedPaymentMode, setSelectedPaymentMode] = useState('');
  const messageFileInputRef = useRef(null);
  const detailRequestRef = useRef(0);

  const load = useCallback(async () => {
    setLoading(true);
    setNotice(null);
    try {
      const response = await api.get('/api/service-orders/my');
      const list = response.success && Array.isArray(response.data) ? response.data : [];
      setOrders(list);
      onCountChange?.(list.length);
    } catch (error) {
      setNotice({
        type: 'error',
        text: error.data?.message || error.message || t('customerUi.serviceOrders.loadError'),
      });
    } finally {
      setLoading(false);
    }
  }, [onCountChange, t]);

  const loadDetail = useCallback(async (id) => {
    if (!id) return;
    const requestId = detailRequestRef.current + 1;
    detailRequestRef.current = requestId;
    setBusy('detail');
    setDetailError('');
    setNotice(null);
    setSelected(null);

    try {
      const response = await api.get(`/api/service-orders/${id}`);
      if (detailRequestRef.current !== requestId) return;
      setSelected(response.data);
      setSelectedPaymentMode(initialPaymentMode(response.data));
    } catch (error) {
      if (detailRequestRef.current !== requestId) return;
      setDetailError(error.data?.message || error.message || t('customerUi.serviceOrders.detailError'));
    } finally {
      if (detailRequestRef.current === requestId) setBusy('');
    }
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    setMessage('');
    setMessageFiles([]);
    setHistoryPage(1);
    setDetailError('');
    setNotice(null);
    setSelectedPaymentMode('');
    if (messageFileInputRef.current) messageFileInputRef.current.value = '';

    if (orderId) {
      loadDetail(orderId);
      return;
    }

    detailRequestRef.current += 1;
    setSelected(null);
    setBusy((current) => (current === 'detail' ? '' : current));
  }, [loadDetail, orderId]);

  const filtered = useMemo(() => orders.filter((order) => {
    if (filter === 'all') return true;
    if (filter === 'completed') return ['completed', 'cancelled'].includes(order.status);
    return !['completed', 'cancelled'].includes(order.status);
  }), [filter, orders]);

  const acceptQuote = async () => {
    if (!selected) return;
    setBusy('accept');
    setNotice(null);
    try {
      const response = await api.post(`/api/service-orders/${selected._id}/accept-quote`, {});
      setSelected(response.data);
      setSelectedPaymentMode(initialPaymentMode(response.data));
      setOrders((current) => current.map((item) => (
        entityId(item) === entityId(response.data) ? response.data : item
      )));
      setNotice({ type: 'success', text: response.message });
    } catch (error) {
      setNotice({ type: 'error', text: error.data?.message || error.message });
    } finally {
      setBusy('');
    }
  };

  const pay = async () => {
    if (!selected) return;
    setBusy('payment');
    setNotice(null);
    try {
      let currentOrder = selected;
      if (
        selectedPaymentMode
        && selectedPaymentMode !== selected.paymentSchedule?.mode
      ) {
        const modeResponse = await api.post(
          `/api/service-orders/${selected._id}/payment-mode`,
          { mode: selectedPaymentMode }
        );
        currentOrder = modeResponse.data;
        setSelected(currentOrder);
        setOrders((items) => items.map((item) => (
          entityId(item) === entityId(currentOrder) ? currentOrder : item
        )));
      }
      const response = await api.post(`/api/service-orders/${currentOrder._id}/payment-request`, {});
      window.location.assign(response.data.paymentUrl);
    } catch (error) {
      setNotice({ type: 'error', text: error.data?.message || error.message });
      setBusy('');
    }
  };

  const sendMessage = async (event) => {
    event.preventDefault();
    if (!selected || (!message.trim() && !messageFiles.length)) return;
    setBusy('message');
    setNotice(null);

    try {
      const body = new FormData();
      body.append('text', message);
      messageFiles.forEach((file) => body.append('attachments', file));
      const response = await api.upload(`/api/service-orders/${selected._id}/messages`, body);
      setSelected(response.data);
      setMessage('');
      setMessageFiles([]);
      setHistoryPage(1);
      if (messageFileInputRef.current) messageFileInputRef.current.value = '';
      setNotice({ type: 'success', text: t('customerUi.serviceOrders.messageSent') });
    } catch (error) {
      setNotice({ type: 'error', text: error.data?.message || error.message });
    } finally {
      setBusy('');
    }
  };

  const download = async (attachment) => {
    if (!selected) return;
    setBusy(entityId(attachment));
    try {
      const result = await api.download(
        `/api/service-orders/${selected._id}/attachments/${attachment._id}`
      );
      const url = URL.createObjectURL(result.blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = result.filename || attachment.originalName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (error) {
      setNotice({ type: 'error', text: error.data?.message || error.message });
    } finally {
      setBusy('');
    }
  };

  const closeDetail = () => {
    detailRequestRef.current += 1;
    setSelected(null);
    setNotice(null);
    setDetailError('');
    setMessage('');
    setMessageFiles([]);
    onCloseOrder?.();
  };

  if (!orderId) {
    return (
      <section className="user-panel-section-card user-service-orders">
        <div className="user-service-orders-heading">
          <div>
            <span><FiBriefcase /></span>
            <div>
              <h3>{t('customerUi.serviceOrders.title')}</h3>
              <p>{t('customerUi.serviceOrders.subtitle')}</p>
            </div>
          </div>
          <button type="button" onClick={load} disabled={loading}>
            <FiRefreshCw /> {loading ? t('customerUi.common.loading') : t('customerUi.common.refresh')}
          </button>
        </div>

        {notice && (
          <div className={`user-service-notice is-${notice.type}`} role="status">
            <FiAlertCircle /> {notice.text}
          </div>
        )}

        <div className="user-service-filters">
          {['active', 'completed', 'all'].map((id) => (
            <button
              type="button"
              className={filter === id ? 'active' : ''}
              onClick={() => setFilter(id)}
              key={id}
            >
              {t(`customerUi.serviceOrders.filters.${id}`)}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="user-panel-loading user-panel-loading--large">
            <span /> {t('customerUi.serviceOrders.loading')}
          </div>
        ) : filtered.length === 0 ? (
          <div className="user-panel-empty">
            <span><FiBriefcase /></span>
            <div>
              <strong>{t('customerUi.serviceOrders.empty')}</strong>
              <p>{t('customerUi.serviceOrders.emptyHint')}</p>
              <a href={localePath('/services')}>{t('customerUi.serviceOrders.viewServices')} <FiArrowLeft /></a>
            </div>
          </div>
        ) : (
          <div className="user-service-order-list">
            {filtered.map((order) => {
              const status = statusInfoLocalized(order.status, t);
              return (
                <button
                  type="button"
                  key={entityId(order)}
                  className="user-service-order-card"
                  onClick={() => onOpenOrder?.(entityId(order))}
                >
                  <span className="user-service-order-icon"><FiBriefcase /></span>
                  <div className="user-service-order-main">
                    <small>#{serviceOrderId(order)} · {formatServiceOrderDate(order.createdAt)}</small>
                    <strong>{order.brief?.projectTitle || order.serviceSnapshot?.title}</strong>
                    <span>{order.serviceSnapshot?.title} · {order.planSnapshot?.name}</span>
                  </div>
                  <div className="user-service-order-progress">
                    <span><i style={{ width: `${Number(order.progress || 0)}%` }} /></span>
                    <small>{t('customerUi.serviceOrders.progress', { value: formatNumber(order.progress) })}</small>
                  </div>
                  <span className={`service-order-status is-${status.tone}`}>{status.label}</span>
                  <FiArrowLeft />
                </button>
              );
            })}
          </div>
        )}
      </section>
    );
  }

  const detailReady = selected && entityId(selected) === String(orderId);

  if (!detailReady) {
    return (
      <article className="user-service-order-page is-loading">
        <header className="user-service-order-page-header">
          <div className="user-service-order-page-heading">
            <span className="user-service-order-page-icon"><FiBriefcase /></span>
            <div>
              <span>{t('customerUi.serviceOrders.tracking')}</span>
              <h2>{detailError ? t('customerUi.serviceOrders.fetchFailed') : t('customerUi.serviceOrders.loadingDetail')}</h2>
              <p>{detailError || t('customerUi.serviceOrders.loadingDetailHint')}</p>
            </div>
          </div>
          <button type="button" className="user-service-order-back" onClick={closeDetail}>
            <FiArrowRight /> {t('customerUi.serviceOrders.back')}
          </button>
        </header>
        <div className="user-service-order-loading-state">
          {detailError ? (
            <>
              <FiAlertCircle />
              <strong>{detailError}</strong>
              <button type="button" onClick={() => loadDetail(orderId)}>
                <FiRefreshCw /> {t('customerUi.common.retry')}
              </button>
            </>
          ) : (
            <>
              <span className="user-service-order-spinner" aria-hidden="true" />
              <strong>{t('customerUi.serviceOrders.wait')}</strong>
            </>
          )}
        </div>
      </article>
    );
  }

  const currentStatus = statusInfoLocalized(selected.status, t);
  const attachments = selected.attachments || [];
  const answers = selected.brief?.answers || [];
  const timeline = [...(selected.timeline || [])].reverse();
  const historyItems = [
    ...attachments.map((attachment) => ({
      id: `attachment-${entityId(attachment)}`,
      type: 'attachment',
      createdAt: attachment.createdAt,
      attachment,
    })),
    ...timeline.map((item) => ({
      id: `timeline-${entityId(item)}`,
      type: 'timeline',
      createdAt: item.createdAt,
      item,
    })),
  ].sort((left, right) => (
    new Date(right.createdAt || 0).getTime() - new Date(left.createdAt || 0).getTime()
  ));
  const historyPageCount = Math.max(1, Math.ceil(historyItems.length / HISTORY_PAGE_SIZE));
  const activeHistoryPage = Math.min(historyPage, historyPageCount);
  const visibleHistory = historyItems.slice(
    (activeHistoryPage - 1) * HISTORY_PAGE_SIZE,
    activeHistoryPage * HISTORY_PAGE_SIZE
  );
  const messages = selected.messages || [];
  const paymentSchedule = selected.paymentSchedule || {};
  const paymentOptions = selected.paymentOptions || [];
  const paymentStages = paymentSchedule.installments || [];
  const nextPaymentStage = nextServicePaymentStage(selected);
  const secondPaymentStage = paymentStages.find((stage) => Number(stage.number) === 2);
  const contractAmount = Number(paymentSchedule.totalAmount)
    || Number(selected.quote?.amount)
    || Number(selected.planSnapshot?.totalAmount)
    || 0;
  const paidAmount = Number(paymentSchedule.paidAmount)
    || (selected.paymentStatus === 'paid' ? contractAmount : 0);
  const remainingAmount = Number(paymentSchedule.remainingAmount)
    || Math.max(0, contractAmount - paidAmount);
  const paymentProgress = contractAmount > 0
    ? Math.min(100, Math.round((paidAmount / contractAmount) * 100))
    : 0;
  const canPayNext = Boolean(
    nextPaymentStage
    && selected.paymentEnabled !== false
    && selected.paymentStatus !== 'paid'
    && selected.status !== 'cancelled'
  );
  const canChoosePaymentMode = Boolean(
    selected.canChangePaymentMode
    && paidAmount <= 0
    && (
      paymentOptions.length > 1
      || !paymentOptions.some((option) => option.id === paymentSchedule.mode)
    )
  );
  const hasSinglePaymentOption = paymentOptions.length === 1;
  const showPaymentMethods = Boolean(
    selected.canChangePaymentMode
    && paidAmount <= 0
    && paymentOptions.length > 0
  );
  const effectivePaymentMode = selectedPaymentMode
    || (hasSinglePaymentOption ? paymentOptions[0].id : '');
  const selectedPaymentOption = paymentOptions.find(
    (option) => option.id === effectivePaymentMode
  );
  const choosingFirstPaymentMode = paymentSchedule.mode === 'review_later';
  const canSubmitPayment = canPayNext || Boolean(
    canChoosePaymentMode
    && selectedPaymentOption
    && effectivePaymentMode !== paymentSchedule.mode
  );
  const currencyLabel = selected.currencyLabel || paymentSchedule.currency || locale.currencyLabel;
  const quoteRequiresCustomerApproval = selected.quote?.requiresCustomerApproval !== false;
  const showQuoteSummary = Boolean(
    selected.quote?.sentAt
    && Number(selected.quote?.amount) > 0
    && (selected.status === 'proposal_sent' || !quoteRequiresCustomerApproval)
  );
  const attachmentMap = new Map(attachments.map((attachment) => [
    entityId(attachment),
    attachment,
  ]));

  return (
    <article className="user-service-order-page">
      <header className="user-service-order-page-header">
        <div className="user-service-order-page-heading">
          <span className="user-service-order-page-icon"><FiBriefcase /></span>
          <div>
            <span>{t('customerUi.serviceOrders.order', { id: serviceOrderId(selected) })} · {formatServiceOrderDate(selected.createdAt)}</span>
            <h2>{selected.brief?.projectTitle || t('customerUi.serviceOrders.defaultOrder')}</h2>
            <p>{selected.serviceSnapshot?.title || t('customerUi.serviceOrders.defaultService')} · {selected.planSnapshot?.name || t('customerUi.serviceOrders.notSelected')}</p>
          </div>
        </div>
        <button type="button" className="user-service-order-back" onClick={closeDetail}>
          <FiArrowRight /> {t('customerUi.serviceOrders.back')}
        </button>
      </header>

      <div className="user-service-order-page-content">
        {notice && (
          <div className={`user-service-notice is-${notice.type}`} role="status">
            <FiAlertCircle /> {notice.text}
          </div>
        )}

        <div className="user-service-order-summary" aria-label={t('customerUi.serviceOrders.summary')}>
          <section className="is-status">
            <span><FiInfo /></span>
            <div><small>{t('customerUi.serviceOrders.currentStatus')}</small><strong>{currentStatus.label}</strong></div>
          </section>
          <section className="is-progress">
            <span><FiClock /></span>
            <div>
              <small>{t('customerUi.serviceOrders.projectProgress')}</small>
              <strong>{formatNumber(selected.progress)}٪</strong>
              <i><b style={{ width: `${Number(selected.progress || 0)}%` }} /></i>
            </div>
          </section>
          <section className="is-quote">
            <span><FiDollarSign /></span>
            <div>
              <small>{t('customerUi.serviceOrders.contractAmount')}</small>
              <strong>
                {contractAmount
                  ? `${formatServiceOrderAmount(contractAmount)} ${currencyLabel}`
                  : t('customerUi.serviceOrders.afterReview')}
              </strong>
            </div>
          </section>
          <section className="is-due-date">
            <span><FiCalendar /></span>
            <div><small>{t('customerUi.serviceOrders.dueDate')}</small><strong>{formatServiceOrderDate(selected.dueDate)}</strong></div>
          </section>
        </div>

        {showQuoteSummary && (
          <section className={`user-service-order-action is-quote${quoteRequiresCustomerApproval ? '' : ' is-direct-quote'}`}>
            <div className="user-service-order-action-icon"><FiDollarSign /></div>
            <div>
              <span>{t(quoteRequiresCustomerApproval ? 'customerUi.serviceOrders.quote.ready' : 'customerUi.serviceOrders.quote.directReady')}</span>
              <strong>{formatServiceOrderAmount(selected.quote?.amount, numberLocale)} <small>{currencyLabel}</small></strong>
              <p>{selected.quote?.details || t(quoteRequiresCustomerApproval ? 'customerUi.serviceOrders.quote.details' : 'customerUi.serviceOrders.quote.directDetails')}</p>
              <small>
                {t('customerUi.serviceOrders.quote.duration', {
                  duration: selected.quote?.duration || t('customerUi.serviceOrders.quote.coordinated'),
                })}
                {selected.quote?.validUntil && ` · ${t('customerUi.serviceOrders.quote.validUntil', { date: formatServiceOrderDate(selected.quote.validUntil, {}, numberLocale) })}`}
              </small>
            </div>
            {quoteRequiresCustomerApproval && (
              <button type="button" onClick={acceptQuote} disabled={busy === 'accept'}>
                <FiCheck /> {busy === 'accept' ? t('customerUi.serviceOrders.quote.accepting') : t('customerUi.serviceOrders.quote.accept')}
              </button>
            )}
          </section>
        )}

        {showPaymentMethods && (
          <section className={`user-service-payment-methods${hasSinglePaymentOption ? ' is-single-option' : ''}`}>
            <header>
              <div>
                <span>{hasSinglePaymentOption ? <FiCreditCard /> : <FiRefreshCw />}</span>
                <div>
                  <h3>
                    {hasSinglePaymentOption
                      ? t('customerUi.serviceOrders.payment.orderPayment')
                      : choosingFirstPaymentMode
                        ? t('customerUi.serviceOrders.payment.choose')
                        : t('customerUi.serviceOrders.payment.chooseAgain')}
                  </h3>
                  <p>
                    {hasSinglePaymentOption
                      ? t('customerUi.serviceOrders.payment.selectedHint', {
                        method: paymentModeCopy[paymentOptions[0]?.id]?.title || t('customerUi.serviceOrders.payment.selectedMethod'),
                      })
                      : choosingFirstPaymentMode
                        ? t('customerUi.serviceOrders.payment.chooseHint')
                        : t('customerUi.serviceOrders.payment.changeHint')}
                  </p>
                </div>
              </div>
              <small>
                {hasSinglePaymentOption
                  ? t('customerUi.serviceOrders.payment.secure')
                  : t('customerUi.serviceOrders.payment.beforeFirst')}
              </small>
            </header>
            <div className="user-service-payment-options">
              {paymentOptions.map((option) => {
                const copy = paymentModeCopy[option.id] || {
                  title: option.id,
                  description: '',
                };
                const isSelected = effectivePaymentMode === option.id;
                return (
                  <label
                    className={isSelected ? 'is-selected' : ''}
                    key={option.id}
                  >
                    <input
                      type="radio"
                      name="service-payment-mode"
                      value={option.id}
                      checked={isSelected}
                      onChange={(event) => setSelectedPaymentMode(event.target.value)}
                    />
                    <span><i>{isSelected && <FiCheck />}</i></span>
                    <div>
                      <strong>{copy.title}</strong>
                      <p>{copy.description}</p>
                      <small>
                        {t('customerUi.serviceOrders.payment.currentAmount', {
                          amount: formatServiceOrderAmount(option.amount, numberLocale),
                          currency: currencyLabel,
                        })}
                        {option.stageCount > 1
                          && ` · ${t('customerUi.serviceOrders.payment.stages', { count: formatNumber(option.stageCount) })}`}
                      </small>
                    </div>
                  </label>
                );
              })}
            </div>
            {!hasSinglePaymentOption && (
              <p className="user-service-payment-methods-note">
                {t('customerUi.serviceOrders.payment.attemptsRemain')}
              </p>
            )}

            {canSubmitPayment && (
              <div className="user-service-payment-cta">
                <div className="user-service-payment-cta-icon"><FiCreditCard /></div>
                <div className="user-service-payment-cta-copy">
                  <span>{paidAmount > 0 ? t('customerUi.serviceOrders.payment.balanceReady') : t('customerUi.serviceOrders.payment.orderReady')}</span>
                  <strong>
                    {formatServiceOrderAmount(
                      effectivePaymentMode !== paymentSchedule.mode && selectedPaymentOption
                        ? selectedPaymentOption.amount
                        : nextPaymentStage?.amount,
                      numberLocale
                    )} <small>{currencyLabel}</small>
                  </strong>
                  <p>
                    {effectivePaymentMode !== paymentSchedule.mode && selectedPaymentOption
                      ? t('customerUi.serviceOrders.payment.selectedHint', {
                        method: paymentModeCopy[effectivePaymentMode]?.title || t('customerUi.serviceOrders.payment.selectedMethod'),
                      })
                      : t('customerUi.serviceOrders.payment.stageHint', {
                        stage: nextPaymentStage?.label || t('customerUi.serviceOrders.payment.orderPayment'),
                      })}
                  </p>
                </div>
                <button type="button" onClick={pay} disabled={busy === 'payment'}>
                  <FiCreditCard /> {
                    busy === 'payment'
                      ? t('customerUi.serviceOrders.payment.connecting')
                      : paidAmount > 0
                        ? t('customerUi.serviceOrders.payment.payBalance')
                        : t('customerUi.serviceOrders.payment.online')
                  }
                </button>
              </div>
            )}
          </section>
        )}

        {canSubmitPayment && !showPaymentMethods && (
          <section className="user-service-order-action is-payment">
            <div className="user-service-order-action-icon"><FiCreditCard /></div>
            <div>
              <span>{paidAmount > 0 ? t('customerUi.serviceOrders.payment.balanceReady') : t('customerUi.serviceOrders.payment.orderReady')}</span>
              <strong>
                {formatServiceOrderAmount(
                  effectivePaymentMode !== paymentSchedule.mode && selectedPaymentOption
                    ? selectedPaymentOption.amount
                    : nextPaymentStage?.amount,
                  numberLocale
                )} <small>{currencyLabel}</small>
              </strong>
              <p>
                {effectivePaymentMode !== paymentSchedule.mode && selectedPaymentOption
                  ? t('customerUi.serviceOrders.payment.selectedHint', {
                    method: paymentModeCopy[effectivePaymentMode]?.title || t('customerUi.serviceOrders.payment.selectedMethod'),
                  })
                  : t('customerUi.serviceOrders.payment.stageHint', {
                    stage: nextPaymentStage?.label || t('customerUi.serviceOrders.payment.orderPayment'),
                  })}
              </p>
              <small>
                {t('customerUi.serviceOrders.payment.secure')}
                {remainingAmount > 0 && ` · ${formatServiceOrderAmount(remainingAmount, numberLocale)} ${currencyLabel}`}
              </small>
            </div>
            <button type="button" onClick={pay} disabled={busy === 'payment'}>
              <FiCreditCard /> {
                busy === 'payment'
                  ? t('customerUi.serviceOrders.payment.connecting')
                  : paidAmount > 0
                    ? t('customerUi.serviceOrders.payment.payBalance')
                    : t('customerUi.serviceOrders.payment.online')
              }
            </button>
          </section>
        )}

        {contractAmount > 0 && (
          <section className="user-service-payment-overview">
            <header>
              <div>
                <span><FiCreditCard /></span>
                <div>
                  <h3>{t('customerUi.serviceOrders.payment.overview')}</h3>
                  <p>{paymentStatusLabelLocalized(selected.paymentStatus, t)}</p>
                </div>
              </div>
              <strong>{t('customerUi.serviceOrders.payment.paidPercent', { percent: formatNumber(paymentProgress) })}</strong>
            </header>
            <div className="user-service-payment-progress">
              <span><i style={{ width: `${paymentProgress}%` }} /></span>
              <dl>
                <div><dt>{t('customerUi.serviceOrders.payment.total')}</dt><dd>{formatServiceOrderAmount(contractAmount, numberLocale)} {currencyLabel}</dd></div>
                <div><dt>{t('customerUi.serviceOrders.payment.paid')}</dt><dd>{formatServiceOrderAmount(paidAmount, numberLocale)} {currencyLabel}</dd></div>
                <div><dt>{t('customerUi.serviceOrders.payment.balance')}</dt><dd>{formatServiceOrderAmount(remainingAmount, numberLocale)} {currencyLabel}</dd></div>
              </dl>
            </div>
            {paymentSchedule.mode === 'two_stage' && remainingAmount > 0 && (
              <div className="user-service-second-payment-due">
                <FiCalendar />
                <span>
                  <small>{t('customerUi.serviceOrders.payment.secondDue')}</small>
                  <strong>
                    {secondPaymentStage?.dueAt || paymentSchedule.secondPaymentDueAt
                      ? formatServiceOrderDate(
                        secondPaymentStage?.dueAt || paymentSchedule.secondPaymentDueAt,
                        {},
                        numberLocale
                      )
                      : secondPaymentStage?.dueType === 'before_delivery'
                        ? t('customerUi.serviceOrders.payment.beforeDelivery')
                        : t('customerUi.serviceOrders.payment.dueAfterStart')}
                  </strong>
                </span>
              </div>
            )}
          </section>
        )}

        <div className="user-service-order-detail-grid">
          <section className="user-service-order-card-detail">
            <header>
              <span><FiInfo /></span>
              <div><h3>{t('customerUi.serviceOrders.requestInfo')}</h3><p>{t('customerUi.serviceOrders.requestInfoHint')}</p></div>
            </header>
            <p className="is-preline">{selected.brief?.description || t('customerUi.serviceOrders.noDescription')}</p>
            <dl>
              <div><dt>{t('customerUi.serviceOrders.service')}</dt><dd>{selected.serviceSnapshot?.title || '—'}</dd></div>
              <div>
                <dt>{selected.planSnapshot?.selectionType === 'quote_request' ? t('customerUi.serviceOrders.requestType') : t('customerUi.serviceOrders.plan')}</dt>
                <dd>{selected.planSnapshot?.name || '—'} · {selected.planSnapshot?.priceLabel || t('customerUi.serviceOrders.noInitialPrice')}</dd>
              </div>
              <div>
                <dt>{t('customerUi.serviceOrders.preferredContact')}</dt>
                <dd>{t(`customerUi.serviceOrders.contact.${selected.contact?.preferredMethod}`, { defaultValue: selected.contact?.preferredMethod || '—' })}</dd>
              </div>
              <div><dt>{t('customerUi.serviceOrders.desiredDate')}</dt><dd>{formatServiceOrderDate(selected.brief?.targetDate)}</dd></div>
              <div><dt>{t('customerUi.serviceOrders.budget')}</dt><dd>{selected.brief?.budgetRange || t('customerUi.serviceOrders.notProvided')}</dd></div>
              {answers.map((answer) => (
                <div key={answer.fieldId}>
                  <dt>{answer.label}</dt>
                  <dd>{answerValue(answer.value, t)}</dd>
                </div>
              ))}
            </dl>
            {!!selected.brief?.referenceLinks?.length && (
              <div className="user-service-order-links">
                <h4><FiLink /> {t('customerUi.serviceOrders.referenceLinks')}</h4>
                {selected.brief.referenceLinks.map((link) => (
                  <a href={link} target="_blank" rel="noreferrer" key={link}>{link}</a>
                ))}
              </div>
            )}
          </section>

          <section className="user-service-order-card-detail">
            <header>
              <span><FiFile /></span>
              <div><h3>{t('customerUi.serviceOrders.history')}</h3><p>{t('customerUi.serviceOrders.historyHint')}</p></div>
            </header>
            {visibleHistory.length ? (
              <div className="user-service-order-history">
                {visibleHistory.map((historyItem) => historyItem.type === 'attachment' ? (
                  <button
                    type="button"
                    className="user-service-order-history-file"
                    key={historyItem.id}
                    onClick={() => download(historyItem.attachment)}
                    disabled={busy === entityId(historyItem.attachment)}
                  >
                    <FiFile />
                    <span>
                      <strong>{historyItem.attachment.originalName}</strong>
                      <small>
                        {(historyItem.attachment.size / 1024 / 1024).toLocaleString('fa-IR', {
                          maximumFractionDigits: 2,
                        })} MB · {formatServiceOrderDate(historyItem.createdAt)}
                      </small>
                    </span>
                    <FiDownload />
                  </button>
                ) : (
                  <div className="user-service-order-history-event" key={historyItem.id}>
                    <span />
                    <div>
                      <strong>{historyItem.item.title}</strong>
                      {historyItem.item.message && <p>{historyItem.item.message}</p>}
                      <small>{formatServiceOrderDate(historyItem.item.createdAt)}</small>
                    </div>
                  </div>
                ))}
              </div>
            ) : <p className="user-service-muted">{t('customerUi.serviceOrders.historyEmpty')}</p>}
            {historyPageCount > 1 && (
              <nav className="user-service-history-pagination" aria-label={t('customerUi.serviceOrders.historyPagination')}>
                <button
                  type="button"
                  onClick={() => setHistoryPage((page) => Math.max(1, page - 1))}
                  disabled={activeHistoryPage === 1}
                >
                  <FiArrowRight /> {t('customerUi.serviceOrders.newer')}
                </button>
                <span>
                  {t('customerUi.common.page', { page: formatNumber(activeHistoryPage), pages: formatNumber(historyPageCount) })}
                </span>
                <button
                  type="button"
                  onClick={() => setHistoryPage((page) => Math.min(historyPageCount, page + 1))}
                  disabled={activeHistoryPage === historyPageCount}
                >
                  {t('customerUi.serviceOrders.older')} <FiArrowLeft />
                </button>
              </nav>
            )}
          </section>
        </div>

        <section className="user-service-order-card-detail user-service-order-conversation">
          <header>
            <span><FiMessageSquare /></span>
            <div><h3>{t('customerUi.serviceOrders.conversation')}</h3><p>{t('customerUi.serviceOrders.conversationHint')}</p></div>
          </header>
          <div className="user-service-order-message-list">
            {messages.length ? messages.map((item) => {
              const messageAttachments = (item.attachmentIds || [])
                .map((attachmentId) => attachmentMap.get(String(attachmentId)))
                .filter(Boolean);

              return (
                <article key={entityId(item)} className={item.senderRole === 'admin' ? 'is-admin' : 'is-user'}>
                  <header>
                    <strong>{item.senderRole === 'admin' ? t('customerUi.serviceOrders.team') : t('customerUi.serviceOrders.you')}</strong>
                    <small>{formatServiceOrderDate(item.createdAt)}</small>
                  </header>
                  {item.text && <p>{item.text}</p>}
                  {!!messageAttachments.length && (
                    <div className="user-service-message-files">
                      {messageAttachments.map((attachment) => (
                        <button
                          type="button"
                          key={entityId(attachment)}
                          onClick={() => download(attachment)}
                          disabled={busy === entityId(attachment)}
                        >
                          <FiPaperclip /> {attachment.originalName}
                        </button>
                      ))}
                    </div>
                  )}
                </article>
              );
            }) : <p className="user-service-muted">{t('customerUi.serviceOrders.messagesEmpty')}</p>}
          </div>

          <form onSubmit={sendMessage}>
            <textarea
              rows="4"
              value={message}
              onChange={(event) => setMessage(event.target.value)}
              placeholder={t('customerUi.serviceOrders.messagePlaceholder')}
            />
            <div>
              <label>
                <FiPaperclip /> {t('customerUi.serviceOrders.attach')}
                <input
                  ref={messageFileInputRef}
                  type="file"
                  multiple
                  onChange={(event) => setMessageFiles(Array.from(event.target.files || []))}
                />
              </label>
              <small>{messageFiles.map((file) => file.name).join('، ')}</small>
              <button type="submit" disabled={busy === 'message'}>
                <FiSend /> {busy === 'message' ? t('customerUi.serviceOrders.sending') : t('customerUi.serviceOrders.send')}
              </button>
            </div>
          </form>
        </section>
      </div>
    </article>
  );
};

export default UserServiceOrders;
